import { useRef, useState } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { contactContent } from "@/app/content/landingContent";
import type { ContactFormData } from "@/types";
import {
  CONTACT_EMAIL_REGEX,
  CONTACT_FIELD_ORDER,
  CONTACT_MAX_LENGTH,
  CONTACT_MIN_LENGTH,
  CONTACT_PHONE_REGEX,
} from "../../../shared/contactFormValidation";

type StatusMessage = { type: "success" | "error"; text: string } | null;
type ContactApiError = {
  error?: string;
  fieldErrors?: Partial<Record<keyof ContactFormData, string>>;
};

class ContactSubmitError extends Error {
  fieldErrors?: Partial<Record<keyof ContactFormData, string>>;

  constructor(payload: ContactApiError) {
    super(payload.error ?? "Request failed");
    this.name = "ContactSubmitError";
    this.fieldErrors = payload.fieldErrors;
  }
}

const FIELD_LABEL_BY_NAME: Record<keyof ContactFormData, keyof ContactFormData> = {
  name: "name",
  email: "email",
  phone: "phone",
  company: "company",
  message: "message",
  consent: "consent",
};

export function ContactForm() {
  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>();

  const [statusMessage, setStatusMessage] = useState<StatusMessage>(null);
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  const focusErrorSummary = () => {
    window.setTimeout(() => {
      errorSummaryRef.current?.focus();
    }, 0);
  };

  const submitForm = async (data: ContactFormData) => {
    const resp = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (!resp.ok) {
      const errorPayload = (await resp.json().catch(() => ({ error: "Unknown error" }))) as ContactApiError;
      throw new ContactSubmitError(errorPayload);
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      await submitForm(data);
      setShowErrorSummary(false);
      setStatusMessage({ type: "success", text: form.submitSuccess });
      reset();
    } catch (err) {
      if (err instanceof ContactSubmitError && err.fieldErrors) {
        let hasFieldErrors = false;
        for (const field of CONTACT_FIELD_ORDER) {
          const fieldError = err.fieldErrors[field];
          if (fieldError) {
            setError(field, { type: "server", message: fieldError });
            hasFieldErrors = true;
          }
        }

        if (hasFieldErrors) {
          setShowErrorSummary(true);
          setStatusMessage({ type: "error", text: form.submitErrorValidation });
          const firstInvalidField = CONTACT_FIELD_ORDER.find((field) => Boolean(err.fieldErrors?.[field]));
          if (firstInvalidField) {
            setFocus(firstInvalidField);
          }
          focusErrorSummary();
          return;
        }
      }

      setStatusMessage({ type: "error", text: form.submitErrorGeneric });
    }
  };

  const onInvalid = (formErrors: FieldErrors<ContactFormData>) => {
    setShowErrorSummary(true);
    setStatusMessage({ type: "error", text: form.submitErrorValidation });

    const firstInvalidField = CONTACT_FIELD_ORDER.find((field) => Boolean(formErrors[field]));
    if (firstInvalidField) {
      setFocus(firstInvalidField);
    }
    focusErrorSummary();
  };

  const { heading, description, highlights, form } = contactContent;
  const {
    labels,
    placeholders,
    validation,
    consentTextStart,
    consentLinkLabel,
    consentLinkHref,
    consentTextEnd,
    submitIdle,
    submitPending,
    requiredNote,
  } = form;
  const errorFieldNames = CONTACT_FIELD_ORDER.filter((field) => Boolean(errors[field]));

  return (
    <section
      id="contact"
      className="py-20 md:py-24 px-6 bg-gray-50"
      aria-labelledby="contact-heading"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column - Contact Info */}
          <div>
            <h2
              id="contact-heading"
              className="text-[clamp(1.875rem,4vw,3rem)] text-slate-900 mb-8 tracking-tight font-bold"
            >
              {heading}
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-prose">
              {description}
            </p>

            <div className="space-y-8">
              {highlights.map((highlight) => {
                const Icon = highlight.icon;
                return (
                  <div className="flex items-center gap-4" key={highlight.label}>
                    <div
                      className="bg-[#004f4f] text-white w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                      aria-hidden="true"
                    >
                      {Icon && <Icon size={20} strokeWidth={2} />}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        {highlight.label}
                      </h3>
                      {highlight.href ? (
                        <a
                          href={highlight.href}
                          className="text-[#004f4f] hover:text-[#003d3d] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 rounded-md inline-block transition-colors text-base"
                        >
                          {highlight.value}
                        </a>
                      ) : (
                        <p className="text-slate-600 text-base">{highlight.value}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white p-8 md:p-10 rounded-3xl border border-gray-200">
            <form
              onSubmit={(e) => {
                void handleSubmit(onSubmit, onInvalid)(e);
              }}
              aria-labelledby="contact-heading"
              className="space-y-6"
              noValidate
            >
              {showErrorSummary && errorFieldNames.length > 0 && (
                <div
                  ref={errorSummaryRef}
                  tabIndex={-1}
                  role="alert"
                  aria-atomic="true"
                  className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
                >
                  <p className="font-semibold">{form.submitErrorSummaryTitle}</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {errorFieldNames.map((field) => {
                      const fieldError = errors[field];
                      const errorMessage = typeof fieldError?.message === "string" ? fieldError.message : validation.required;
                      return (
                        <li key={field}>
                          <a href={`#${field}`} className="underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-[#004f4f] focus:ring-offset-2 rounded-sm">
                            {labels[FIELD_LABEL_BY_NAME[field]]}: {errorMessage}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <div>
                <label
                  htmlFor="name"
                  className="block text-slate-900 font-semibold mb-2 text-base"
                >
                  {labels.name}
                </label>
                <input
                  type="text"
                  id="name"
                  {...register("name", {
                    required: validation.required,
                    minLength: { value: CONTACT_MIN_LENGTH.name, message: validation.minLength2 },
                    maxLength: { value: CONTACT_MAX_LENGTH.name, message: validation.maxLength100 },
                  })}
                  className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:border-[#004f4f] transition-all text-slate-900 bg-white text-base"
                  placeholder={placeholders.name}
                  autoComplete="name"
                  minLength={CONTACT_MIN_LENGTH.name}
                  maxLength={CONTACT_MAX_LENGTH.name}
                  aria-invalid={errors.name ? "true" : "false"}
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="mt-2 text-sm text-red-600" role="alert">{errors.name.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-slate-900 font-semibold mb-2 text-base"
                  >
                    {labels.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...register("email", {
                      required: validation.required,
                      pattern: {
                        value: CONTACT_EMAIL_REGEX,
                        message: validation.emailPattern,
                      },
                      maxLength: { value: CONTACT_MAX_LENGTH.email, message: validation.maxLength254 },
                    })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:border-[#004f4f] transition-all text-slate-900 bg-white text-base"
                    placeholder={placeholders.email}
                    autoComplete="email"
                    maxLength={CONTACT_MAX_LENGTH.email}
                    aria-invalid={errors.email ? "true" : "false"}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-2 text-sm text-red-600" role="alert">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-slate-900 font-semibold mb-2 text-base"
                  >
                    {labels.phone}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    {...register("phone", {
                      maxLength: { value: CONTACT_MAX_LENGTH.phone, message: validation.maxLength30 },
                      pattern: {
                        value: CONTACT_PHONE_REGEX,
                        message: validation.phonePattern,
                      },
                    })}
                    className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:border-[#004f4f] transition-all text-slate-900 bg-white text-base"
                    placeholder={placeholders.phone}
                    autoComplete="tel"
                    inputMode="tel"
                    maxLength={CONTACT_MAX_LENGTH.phone}
                    aria-invalid={errors.phone ? "true" : "false"}
                    aria-describedby={errors.phone ? "phone-error" : undefined}
                  />
                  {errors.phone && (
                    <p id="phone-error" className="mt-2 text-sm text-red-600" role="alert">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-slate-900 font-semibold mb-2 text-base"
                >
                  {labels.company}
                </label>
                <input
                  type="text"
                  id="company"
                  {...register("company", {
                    maxLength: { value: CONTACT_MAX_LENGTH.company, message: validation.maxLength200 },
                  })}
                  className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:border-[#004f4f] transition-all text-slate-900 bg-white text-base"
                  placeholder={placeholders.company}
                  autoComplete="organization"
                  maxLength={CONTACT_MAX_LENGTH.company}
                  aria-invalid={errors.company ? "true" : "false"}
                  aria-describedby={errors.company ? "company-error" : undefined}
                />
                {errors.company && (
                  <p id="company-error" className="mt-2 text-sm text-red-600" role="alert">{errors.company.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-slate-900 font-semibold mb-2 text-base"
                >
                  {labels.message}
                </label>
                <textarea
                  id="message"
                  {...register("message", {
                    required: validation.required,
                    minLength: { value: CONTACT_MIN_LENGTH.message, message: validation.minLength10 },
                    maxLength: { value: CONTACT_MAX_LENGTH.message, message: validation.maxLength5000 },
                  })}
                  rows={5}
                  className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:border-[#004f4f] transition-all text-slate-900 bg-white resize-none text-base"
                  placeholder={placeholders.message}
                  minLength={CONTACT_MIN_LENGTH.message}
                  maxLength={CONTACT_MAX_LENGTH.message}
                  aria-invalid={errors.message ? "true" : "false"}
                  aria-describedby={errors.message ? "message-error" : undefined}
                />
                {errors.message && (
                  <p id="message-error" className="mt-2 text-sm text-red-600" role="alert">{errors.message.message}</p>
                )}
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="consent"
                  {...register("consent", {
                    required: validation.consent
                  })}
                  className="mt-1 w-5 h-5 border-2 border-gray-300 rounded focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-2 accent-[#004f4f]"
                  aria-invalid={errors.consent ? "true" : "false"}
                  aria-describedby={errors.consent ? "consent-error" : undefined}
                />
                <div>
                  <label
                    htmlFor="consent"
                    className="text-sm text-slate-600 leading-relaxed"
                  >
                    {consentTextStart}
                    <a
                      href={consentLinkHref}
                      className="text-[#004f4f] underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-[#004f4f] focus:ring-offset-2 rounded-sm"
                    >
                      {consentLinkLabel}
                    </a>
                    {consentTextEnd}
                  </label>
                  {errors.consent && (
                    <p id="consent-error" className="mt-1 text-sm text-red-600" role="alert">{errors.consent.message}</p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#004f4f] text-white px-8 py-5 rounded-full hover:bg-[#003d3d] focus:outline-none focus:ring-4 focus:ring-[#004f4f] focus:ring-offset-4 transition-colors font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? submitPending : submitIdle}
              </button>

              <p className="text-sm text-slate-500 text-center">{requiredNote}</p>

              {statusMessage && (
                <div
                  role={statusMessage.type === "error" ? "alert" : "status"}
                  aria-live={statusMessage.type === "error" ? "assertive" : "polite"}
                  aria-atomic="true"
                  className={`p-4 rounded-xl text-sm font-medium ${statusMessage.type === "success"
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : "bg-red-50 text-red-800 border border-red-200"
                    }`}
                >
                  {statusMessage.text}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
